import { apiClient } from '../lib/apiClient';

/**
 * Status of a camera source's device token.
 */
export type CameraSourceStatus = 'active' | 'revoked';

/**
 * A camera source as returned by list/get/update/revoke endpoints.
 * Never includes the device token — the token is only ever present on the
 * response of createCameraSource (see CameraSourceWithToken below).
 */
export interface CameraSource {
  id: string;
  aquariumId: string;
  name: string;
  status: CameraSourceStatus;
  expectedCaptureTimes: readonly string[];
  lastReceivedImageId: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
}

/**
 * A camera source as returned exactly once, from createCameraSource.
 * Includes the raw device token — this value must never be persisted by the
 * caller or re-displayed after the creating component unmounts.
 */
export type CameraSourceWithToken = CameraSource & { token: string };

export interface CreateCameraSourceInput {
  name: string;
  expectedCaptureTimes?: readonly string[];
}

export interface UpdateCameraSourceInput {
  name?: string;
  expectedCaptureTimes?: readonly string[];
}

interface ApiEnvelope<T> {
  data: T;
}

/**
 * List all camera sources for the given aquarium.
 */
export async function listCameraSources(aquariumId: string): Promise<CameraSource[]> {
  const response = await apiClient.get<ApiEnvelope<CameraSource[]>>(
    `/api/v1/aquariums/${aquariumId}/camera-sources`,
  );
  return response.data;
}

/**
 * Create a new camera source for the given aquarium. The response includes a
 * one-time device token — it must be shown to the user exactly once and never
 * fetched again.
 */
export async function createCameraSource(
  aquariumId: string,
  input: CreateCameraSourceInput,
): Promise<CameraSourceWithToken> {
  const response = await apiClient.post<ApiEnvelope<CameraSourceWithToken>>(
    `/api/v1/aquariums/${aquariumId}/camera-sources`,
    input,
  );
  return response.data;
}

/**
 * Update an existing camera source's name and/or expected capture times.
 */
export async function updateCameraSource(
  aquariumId: string,
  id: string,
  input: UpdateCameraSourceInput,
): Promise<CameraSource> {
  const response = await apiClient.patch<ApiEnvelope<CameraSource>>(
    `/api/v1/aquariums/${aquariumId}/camera-sources/${id}`,
    input,
  );
  return response.data;
}

/**
 * Revoke a camera source's device token. This is irreversible — the token
 * stops working immediately.
 */
export async function revokeCameraSource(aquariumId: string, id: string): Promise<CameraSource> {
  const response = await apiClient.post<ApiEnvelope<CameraSource>>(
    `/api/v1/aquariums/${aquariumId}/camera-sources/${id}/revoke`,
    {},
  );
  return response.data;
}

/**
 * Permanently delete a REVOKED camera source (204). The backend answers 409
 * for a source that is still active and 404 for an unknown id. Images the
 * camera already sent stay in the aquarium.
 */
export async function deleteCameraSource(aquariumId: string, id: string): Promise<void> {
  await apiClient.delete<void>(`/api/v1/aquariums/${aquariumId}/camera-sources/${id}`);
}

/**
 * A single aquarium image as returned by GET /aquariums/:aquariumId/images/:imageId
 * (only the fields the camera sources page needs).
 */
export interface CameraSourceImage {
  id: string;
  thumbnailUrl: string | null;
  displayUrl: string | null;
  originalUrl: string | null;
  uploadedAt: string;
  capturedAt: string | null;
}

/**
 * Fetch one image of the aquarium by id — used to show a source's exact
 * last received image (`lastReceivedImageId`).
 */
export async function getCameraSourceImage(
  aquariumId: string,
  imageId: string,
): Promise<CameraSourceImage> {
  const response = await apiClient.get<ApiEnvelope<CameraSourceImage>>(
    `/api/v1/aquariums/${aquariumId}/images/${imageId}`,
  );
  return response.data;
}
